import {useNavigate, useSearchParams} from 'react-router-dom';
import {Trans} from '../../i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {CheckIcon} from '../../icons/material/Check';
import {Button} from '../../ui/buttons/button';

/**
 * Interstitial consent screen for plans that are billed through the legacy
 * Dohaty platform (e.g. Zain KSA subscriptions registered under
 * dohaty-sa.com). Reached via a price's external_landing_page_url, e.g.:
 *
 *   /dohaty-consent?redirect=https%3A%2F%2Fdohaty-sa.com%2Fcheckout%2F9%2F8%2Fphonesub%3Fstatus%3Dstart
 *
 * Kept in its own file/route on purpose: the Dohaty deployment shares this
 * codebase, and its own checkout flows must not be affected. This page only
 * shows the reassurance message and forwards on confirmation.
 */

const DEFAULT_TARGET = 'https://dohaty-sa.com/checkout/9/8/phonesub?status=start';

// only the legacy platform (or a same-site path) may be a forward target —
// an unvalidated ?redirect= on a payment flow would be an open redirect
const ALLOWED_HOSTS = ['dohaty-sa.com', 'www.dohaty-sa.com'];

/**
 * Resolve the forward target through the browser's own URL parser rather than
 * string matching, then check the parsed origin. String checks are not safe
 * here: for http(s) a backslash is a path separator, so "/\evil.com" parses
 * as protocol-relative "//evil.com" and would pass a naive
 * startsWith('/') && !startsWith('//') test while navigating off-site.
 */
function resolveTarget(raw: string | null): string {
  if (!raw || typeof window === 'undefined') return DEFAULT_TARGET;
  const origin = window.location.origin;
  try {
    const url = new URL(raw, origin);
    if (url.origin === origin) {
      // same-site: hand back only the path we parsed, never the raw input
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (url.protocol === 'https:' && ALLOWED_HOSTS.includes(url.hostname)) {
      return url.toString();
    }
  } catch {}
  return DEFAULT_TARGET;
}

export function DohatyConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRedirect = searchParams.get('redirect');

  return (
    <div className="flex min-h-screen items-center justify-center bg-alt px-16">
      <StaticPageTitle>
        <Trans message="متابعة الاشتراك" />
      </StaticPageTitle>
      <div className="w-full max-w-[360px] rounded-2xl border bg px-18 pb-18 pt-24 text-center shadow-xl">
        <div className="mx-auto mb-14 flex h-52 w-52 items-center justify-center rounded-full border-2 border-primary bg-primary/15">
          <CheckIcon size="md" className="text-primary" />
        </div>
        <div className="mb-8 text-base font-extrabold leading-normal">
          <Trans message="عازة هي دوحتي بحلتها الجديدة" />
        </div>
        <div className="mb-8 text-xs font-semibold text-primary">
          <Trans message="نفس الفنانين · نفس المحتوى · تجربة أفضل" />
        </div>
        <div className="mb-16 text-xs leading-relaxed text-muted">
          <Trans message="اشتراكك عبر زين السعودية سيتم من خلال منصة دوحتي" />
        </div>
        <Button
          variant="flat"
          color="primary"
          size="md"
          className="mb-10 w-full"
          onClick={() => {
            // resolved at click time so the check runs in the browser, never
            // during a server render where window is unavailable
            window.location.assign(resolveTarget(rawRedirect));
          }}
        >
          <Trans message="متابعة الاشتراك" />
        </Button>
        <button
          type="button"
          className="text-xs text-muted underline hover:text-main"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/pricing');
            }
          }}
        >
          <Trans message="رجوع" />
        </button>
      </div>
    </div>
  );
}
